import {registerLocaleData} from '@angular/common';
import localeDE from '@angular/common/locales/de';
import localeFR from '@angular/common/locales/fr';
import localeIT from '@angular/common/locales/it';
import localeEN from '@angular/common/locales/en';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi
} from '@angular/common/http';
import {NgModule, inject, provideAppInitializer} from '@angular/core';
import {MatListModule} from '@angular/material/list';
import {MatTooltipModule} from '@angular/material/tooltip';
import {BrowserModule} from '@angular/platform-browser';
import {TranslateModule} from '@ngx-translate/core';
import {
  EXTERNAL_LINK,
  ObDocumentMetaService,
  ObExternalLinkModule,
  ObHttpApiInterceptor,
  ObHttpApiInterceptorConfig,
  ObIconModule,
  ObMasterLayoutConfig,
  ObNavTreeComponent,
  provideObliqueConfiguration
} from '@oblique/oblique';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {SharedModule} from 'shared/shared.module';
import {PendingChangesDialogComponent} from './core/pending-changes-dialog/pending-changes-dialog.component';
import {MAT_TABS_CONFIG, MatTabsConfig} from '@angular/material/tabs';
import {MatPaginatorIntl} from '@angular/material/paginator';
import {MatPaginatorIntlNgxTranslateService} from './core/mat-paginator-intl-ngx-translate/mat-paginator-intl-ngx-translate.service';
import {MatDialogModule} from '@angular/material/dialog';
import {MatStepperIntl} from '@angular/material/stepper';
import {MatStepperIntlNgxTranslateService} from './core/mat-stepper-intl-ngx-translate/mat-stepper-intl-ngx-translate.service';
import {MatIconModule} from '@angular/material/icon';
import {provideMomentDateAdapter} from '@angular/material-moment-adapter';
import {MarkdownDialogComponent} from './changelog/markdown-dialog.component';
import {DataService} from 'shared/services/data.service';

registerLocaleData(localeDE);
registerLocaleData(localeFR);
registerLocaleData(localeIT);
registerLocaleData(localeEN);

export function resetDataInitializer() {
  const dataService = inject(DataService);
  return dataService.resetData().catch(() => Promise.resolve());
}

@NgModule({
  declarations: [
    AppComponent,
    PendingChangesDialogComponent,
    MarkdownDialogComponent
  ],
  exports: [ObIconModule, MatIconModule],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,

    SharedModule,
    TranslateModule,
    AppRoutingModule,
    MatListModule,
    MatTooltipModule,
    MatIconModule,
    ObExternalLinkModule,
    MatDialogModule,
    ObNavTreeComponent
  ],
  providers: [
    provideObliqueConfiguration({
      accessibilityStatement: {
        createdOn: new Date('2026-05-08'),
        applicationName: 'Logib',
        conformity: 'none',
        applicationOperator: 'i18n.common.operation.application',
        contact: [{email: 'logib@ebg.admin.ch'}, {phone: '0800 55 99 00'}]
      },
      translate: {
        additionalFiles: [
          {prefix: './assets/i18n/common/', suffix: '.json'},
          {prefix: './assets/i18n/m1/', suffix: '.json'},
          {prefix: './assets/i18n/m2/', suffix: '.json'}
        ]
      }
    }),
    {provide: HTTP_INTERCEPTORS, useClass: ObHttpApiInterceptor, multi: true},
    {provide: EXTERNAL_LINK, useValue: {icon: 'right'}},
    {
      provide: MAT_TABS_CONFIG,
      useValue: {disablePagination: true} as MatTabsConfig
    },
    {
      provide: MatPaginatorIntl,
      useClass: MatPaginatorIntlNgxTranslateService
    },
    {provide: MatStepperIntl, useClass: MatStepperIntlNgxTranslateService},
    provideHttpClient(withInterceptorsFromDi()),
    provideMomentDateAdapter(),
    provideAppInitializer(resetDataInitializer)
  ]
})
export class AppModule {
  constructor(
    readonly documentMetaService: ObDocumentMetaService,
    readonly config: ObMasterLayoutConfig,
    private readonly obliqueHttpInterceptorConfig: ObHttpApiInterceptorConfig
  ) {
    documentMetaService.titleSuffix = 'i18n.application.title';
    documentMetaService.description = 'i18n.application.description';
    config.locale.locales = ['de', 'fr', 'it', 'en'];
    config.footer.isSticky = true;
    config.layout.hasLayout = true;
    this.obliqueHttpInterceptorConfig.timeout = 300000;
    this.config.homePageRoute = '/';
  }
}
